/**
 * The order of a table's columns, as a person dragged it.
 *
 * The owner, 2026-10-15, on the outreach tables: copy Blockcircle's fully
 * adjustable table, columns that resize and reorder, rather than reinvent it.
 * Resizing came first (`column-width-model.ts`, 2026-10-20). This is the
 * other half, the way Blockcircle's own tables do it: a header is picked up
 * and dropped on another, landing before it when dropped on its left half and
 * after it on its right half, and the order is remembered per table in this
 * browser beside the widths and the hidden columns.
 *
 * WHAT MOVES. The first column stays first. It names the row, carries the
 * row's link and its checkbox, and on a wide table it is the one pinned while
 * the rest scroll, so a table whose first column could be swapped out would
 * lose all three. A `fixed` column (a row's actions) and the `+ Add column`
 * cell keep their places too. Everything else moves, among the slots the
 * others leave.
 *
 * A STORED ORDER OUTLIVES THE COLUMNS IT WAS MADE FOR. A column a later build
 * adds is not in it, and one a build removes still is. The stored keys that
 * still exist keep their order, a key that is gone is dropped, and a new one
 * goes in after the column it follows by default, so a person's arrangement
 * survives a release and the new column turns up where the page put it.
 */
import { columnWidthStore, type ColumnWidthStore } from './column-width-model'

/** What the model needs to know about a column. `Column<T>` from `Table.tsx` fits. */
export interface OrderableColumn {
  key: string
  fixed?: boolean
  kind?: 'data' | 'add'
}

/** A movable column's keys in the order a person left them. Empty is the page's own order. */
export type ColumnOrder = readonly string[]

/** Whether the column at `index` of the page's list can be dragged to another place. */
export function isMovableColumn(column: OrderableColumn, index: number): boolean {
  return index > 0 && column.fixed !== true && column.kind !== 'add'
}

/** The movable columns' keys, in the page's own order. */
export function movableKeys(columns: readonly OrderableColumn[]): string[] {
  return columns.filter((column, index) => isMovableColumn(column, index)).map((column) => column.key)
}

/**
 * The stored order made to fit the columns the page has now.
 *
 * Stored keys that still name a movable column keep their order. A movable
 * column the stored order does not mention goes in after the column before it
 * in the page's own order, or first when nothing before it is placed yet.
 */
export function reconcileOrder(columns: readonly OrderableColumn[], stored: ColumnOrder): string[] {
  const defaults = movableKeys(columns)
  const known = new Set(defaults)
  const result = [...new Set(stored)].filter((key) => known.has(key))
  defaults.forEach((key, at) => {
    if (result.includes(key)) return
    let anchor = -1
    for (let back = at - 1; back >= 0; back -= 1) {
      const before = defaults[back]
      if (before === undefined) continue
      const found = result.indexOf(before)
      if (found !== -1) {
        anchor = found
        break
      }
    }
    result.splice(anchor + 1, 0, key)
  })
  return result
}

/**
 * The page's columns in the order a person left them.
 *
 * Columns that do not move stay in their own slots and the movable ones fill
 * the rest, in the reconciled order. With nothing stored this is the page's
 * own list, unchanged.
 */
export function orderColumns<T extends OrderableColumn>(columns: readonly T[], stored: ColumnOrder): T[] {
  if (stored.length === 0) return [...columns]
  const order = reconcileOrder(columns, stored)
  const byKey = new Map(columns.map((column) => [column.key, column]))
  const queue = order.map((key) => byKey.get(key)).filter((column): column is T => column !== undefined)
  return columns.map((column, index) => (isMovableColumn(column, index) ? (queue.shift() ?? column) : column))
}

/** Which side of a header a drop lands on: its left half is before it, its right half after. */
export function dropSide(pointerX: number, left: number, width: number): 'before' | 'after' {
  return pointerX < left + width / 2 ? 'before' : 'after'
}

/**
 * The order after `moving` is dropped on `target`.
 *
 * Works on the reconciled order, so it is right whether or not anything was
 * stored before. A drop on itself, or on or of a column that does not move,
 * changes nothing.
 */
export function moveColumn(
  columns: readonly OrderableColumn[],
  stored: ColumnOrder,
  moving: string,
  target: string,
  side: 'before' | 'after',
): string[] {
  const order = reconcileOrder(columns, stored)
  if (moving === target || !order.includes(moving) || !order.includes(target)) return order
  const without = order.filter((key) => key !== moving)
  const at = without.indexOf(target)
  without.splice(side === 'before' ? at : at + 1, 0, moving)
  return without
}

/** The order after one column steps left (`-1`) or right (`1`), for the keyboard and the columns picker. */
export function stepColumn(columns: readonly OrderableColumn[], stored: ColumnOrder, key: string, step: -1 | 1): string[] {
  const order = reconcileOrder(columns, stored)
  const at = order.indexOf(key)
  const to = at + step
  if (at === -1 || to < 0 || to >= order.length) return order
  const next = [...order]
  next[at] = next[to] as string
  next[to] = key
  return next
}

/** Whether a stored order says anything the page's own order does not. */
export function isReordered(columns: readonly OrderableColumn[], stored: ColumnOrder): boolean {
  const order = reconcileOrder(columns, stored)
  const defaults = movableKeys(columns)
  return order.some((key, at) => key !== defaults[at])
}

/** Where one table's order is kept, beside its widths and its hidden columns. */
export function columnOrderKey(tableKey: string): string {
  return `gojibasel.table.${tableKey}.order`
}

/** A stored value, read back as an order. Anything that is not a list of keys reads as none. */
export function parseColumnOrder(raw: string | null | undefined): string[] {
  if (raw === null || raw === undefined || raw === '') return []
  try {
    const value: unknown = JSON.parse(raw)
    if (!Array.isArray(value)) return []
    return [...new Set(value.filter((item): item is string => typeof item === 'string' && item !== ''))]
  } catch {
    return []
  }
}

/** The stored order, or none. Never throws: a browser that blocks site data reads as a table never reordered. */
export function readColumnOrder(store: ColumnWidthStore | undefined, tableKey: string): string[] {
  try {
    return parseColumnOrder(store?.getItem(columnOrderKey(tableKey)))
  } catch {
    return []
  }
}

/** Keeps the order, or forgets it when it is empty. Returns whether the write landed. */
export function writeColumnOrder(store: ColumnWidthStore | undefined, tableKey: string, order: ColumnOrder): boolean {
  if (store === undefined) return false
  try {
    if (order.length === 0) store.removeItem(columnOrderKey(tableKey))
    else store.setItem(columnOrderKey(tableKey), JSON.stringify([...new Set(order)]))
    return true
  } catch {
    return false
  }
}

/** The same storage the widths use. */
export const columnOrderStore = columnWidthStore
